import { IconButton, styled, Typography } from '@ethings/ui-toolkit';
import { ZoomControls } from '@ethings/ui-toolkit/map';
import { Box, useTheme } from '@mui/material';
import chroma from 'chroma-js';
import L, { Map } from 'leaflet';
import {
  CSSProperties,
  FC,
  PropsWithChildren,
  ReactNode,
  RefObject,
  useId,
  useRef,
  type JSX,
} from 'react';
import { useTranslation } from 'react-i18next';
import { MapContainer, Marker, Tooltip } from 'react-leaflet';
import { MapProvider, useMapZoomLimits, useSmallScreen } from '../../hooks';
import { SearchProvider } from '../../pages/list/use-search';
import { ToggleLayers } from '../../types';
import { getMarkerTemplate, ValidateResize } from '../../util';
import { FlexBox } from '../flex-box';
import { Icon } from '../icons';
import { MapDiscoverItem } from '../search-thing-map';
import { MapLayersProvider } from './hooks';
import { InlayMapSearch } from './inlay-map-search';
import { MapLayerController } from './map-layer-controller';
import { SyncZoomLimits } from './sync-zoom-limits';
import { ViewMapButton } from './view-map-button';

interface Props extends PropsWithChildren {
  'data-testid'?: string;
  allowFullScreen?: boolean;
  allowSearch?: boolean;
  blurred?: boolean;
  center?: L.LatLng;
  customMarker?: ReactNode;
  disableRemountOnChange?: boolean;
  disabledLayers?: ToggleLayers[];
  disableSearchMapAnimation?: boolean;
  drawerLayerSelection?: boolean;
  entityId?: string;
  marker?: ReactNode;
  onAddressClick?: (item: MapDiscoverItem) => void;
  onOpenInFullClick?: () => void;
  overlay?: JSX.Element;
  overlayLabel?: string;
  pinLabel?: ReactNode;
  renderMapLayersProvider?: boolean;
  scrollWheelZoom?: boolean;
  showMarker?: boolean;
  static?: boolean;
  style?: CSSProperties;
  zoom: number;
  shouldUseLocalStorage?: boolean;
}

interface MarkerProps extends PropsWithChildren {
  center: L.LatLng;
  pinLabelPosition?: L.Direction;
}

const MarkerFormatted: FC<MarkerProps> = ({ center, children }) => {
  const icon = getMarkerTemplate();
  return <Marker icon={icon} position={center}>{children}</Marker>;
};

const MaximizeButton = styled(IconButton)(({ theme }) => ({
  backgroundColor: theme.palette.primary.contrastText,
  boxShadow: theme.shadows[3],
  '&:hover': {
    backgroundColor: theme.palette.grey['50'],
  },
}));

const InlayMapRoot = styled(Box)<{ blurred?: boolean }>(({ blurred }) => {
  const theme = useTheme();
  const borderRadius = theme.shape.borderRadius * 3;
  const buttonShadow = theme.shadows[3];

  return {
    '& .leaflet-map-pane': { zIndex: blurred ? ('inherit' as const) : ('400' as const) },
    '& .draw-control-disabled': {
      backgroundColor: theme.map.editControl.disabled,
      cursor: 'not-allowed',
      pointerEvents: 'auto',
    },
    '& .leaflet-container': { height: '100%', width: '100%' },
    '& .leaflet-container a': { color: theme.palette.common.white },
    '& .leaflet-control': { marginTop: 0 },
    '& .leaflet-div-icon': { display: 'flex', background: 'none', border: 'none' },
    '& .leaflet-editing-icon': { backgroundColor: theme.palette.common.white, borderStyle: 'solid', borderWidth: '1px' },
    '& .leaflet-draw-tooltip:not(.leaflet-error-draw-tooltip)': { display: 'none' },
    '& .leaflet-marker-icon': { outline: 'none' },
    '& .leaflet-interactive:focus': { outline: 'none' },
    '& .leaflet-popup-content': { margin: 0 },
    '& .leaflet-popup-content-wrapper': { padding: 0 },
    '& .leaflet-tooltip': { backgroundColor: chroma(theme.palette.background.default).alpha(0.8).css(), borderRadius: theme.shape.borderRadius },
    '& .leaflet-top': { top: '50%', transform: 'translateY(-50%)' },
    '& .leaflet-draw-toolbar': {
      boxShadow: buttonShadow,
      borderRadius,
      padding: 0,
      backgroundColor: theme.palette.background.paper,
    },
    '& .leaflet-draw-toolbar:has(a.draw-control-disabled)': {
      borderColor: theme.palette.divider,
    },
    '& .leaflet-draw-toolbar a': {
      width: 42,
      height: 48,
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      padding: 0,
      margin: 0,
      backgroundImage: 'none',
      backgroundColor: theme.palette.background.paper,
      color: theme.palette.primary.main,
      boxShadow: 'none',
    },
    '& .leaflet-draw-toolbar a::before, & .leaflet-draw-toolbar a::after': {
      content: 'none',
      display: 'none',
    },
    '& .leaflet-draw-toolbar a span': { display: 'none' },
    '& .leaflet-draw-toolbar a:hover': {
      backgroundColor: theme.palette.primary.contrastText,
    },
    '& .leaflet-draw-toolbar a:focus, & .leaflet-draw-toolbar a:focus-visible': {
      outline: 'none',
    },
    '& .leaflet-draw-toolbar a.draw-control-disabled': {
      color: theme.palette.action.disabled,
      cursor: 'not-allowed',
      pointerEvents: 'auto',
    },
    '& .leaflet-touch .leaflet-draw-actions': {
      top: 'calc(100% - 100px)',
      left: theme.spacing(5),
      backgroundColor: 'transparent',
    },

    '& .leaflet-touch .leaflet-control-layers, .leaflet-touch .leaflet-bar': {
      border: 'none',
    },

    '& .leaflet-touch .leaflet-bar a:first-child': {
      borderTopLeftRadius: `${borderRadius}px`,
      borderTopRightRadius: `${borderRadius}px`,
    },
    '& .leaflet-touch .leaflet-bar a:last-child': {
      borderBottomLeftRadius: `${borderRadius}px`,
      borderBottomRightRadius: `${borderRadius}px`,
    },
    '& .leaflet-touch .leaflet-draw-actions a': {
      backgroundColor: theme.palette.background.paper,
      borderRadius: `${theme.shape.borderRadius}px`,
      boxShadow: buttonShadow,
      color: theme.palette.primary.main,
      marginLeft: theme.spacing(0.5),
      marginTop: theme.spacing(0.5),
    },
    '& .leaflet-touch .leaflet-draw-actions a:hover': {
      backgroundColor: theme.palette.primary.light,
      color: theme.palette.primary.contrastText,
    },
    '& .leaflet-overlay-pane': { filter: blurred ? 'blur(4px)' : undefined },
    position: 'relative',
    boxShadow: theme.shadows[1],
    borderRadius: `${theme.shape.borderRadius}px`,
    height: '100%',
    width: '100%',
    overflow: 'hidden',
  };
});

export const InlayMap: FC<Props> = ({
  allowFullScreen = false,
  allowSearch,
  blurred,
  center,
  children,
  customMarker,
  disableRemountOnChange = false,
  disabledLayers = [],
  disableSearchMapAnimation,
  drawerLayerSelection = false,
  entityId,
  marker,
  onAddressClick,
  onOpenInFullClick,
  overlay,
  overlayLabel,
  pinLabel,
  renderMapLayersProvider = true,
  scrollWheelZoom,
  showMarker = true,
  static: staticMap,
  style,
  zoom,
  shouldUseLocalStorage,
  ...props
}) => {
  const { t } = useTranslation(['common']);
  const theme = useTheme();
  const smallScreen = useSmallScreen();
  const mapRef = useRef<Map>(null);
  const id = useId();
  const { minZoom, maxZoom } = useMapZoomLimits();

  const resizeMap = (mapRef: RefObject<Map | null>): void => {
    const resizeObserver = new ResizeObserver(() => mapRef.current?.invalidateSize());
    const container = document.getElementById(id);
    if (container) {
      resizeObserver.observe(container);
    }
  };

  const body = (
    <MapProvider>
      <InlayMapRoot blurred={blurred}>
        <FlexBox className="InlayMap-overlayBlocker" sx={{ display: staticMap ? 'block' : 'none', height: '100%', position: 'absolute', width: '100%', zIndex: 501 }} />
        {overlay && <FlexBox sx={{ position: 'absolute', width: '100%', left: 0, top: theme.spacing(2), zIndex: 999, justifyContent: 'center' }}>{overlay}</FlexBox>}
        <FlexBox data-testid={props['data-testid']} sx={{ filter: !center && !allowSearch ? 'blur(4px)' : undefined, height: 1 }}>
          <MapContainer
            id={id}
            ref={mapRef}
            whenReady={() => resizeMap(mapRef)}
            key={!disableRemountOnChange ? new Date().getTime() : ''}
            boxZoom={!blurred}
            center={center ?? L.latLng(0, 0)}
            doubleClickZoom={!blurred}
            dragging={!blurred}
            maxBoundsViscosity={0.8}
            maxZoom={maxZoom}
            minZoom={minZoom}
            preferCanvas={true}
            renderer={new L.Canvas()}
            scrollWheelZoom={scrollWheelZoom}
            touchZoom={!blurred}
            zoom={center ? zoom : minZoom}
            zoomAnimation={true}
            zoomControl={false}
            zoomDelta={1}
            zoomSnap={0.5}
          >
            <SearchProvider>
              <SyncZoomLimits />
              <ValidateResize />
              <MapLayerController
                disabledLayers={disabledLayers}
                drawerLayerSelection={drawerLayerSelection}
                layerSelectionButtonSx={{ position: 'absolute', right: theme.spacing(2), bottom: 116, zIndex: 500 }}
                shouldShowLayerSelection={center && !blurred}
              />
              {center && marker}
              {center && showMarker
                && <>
                  {customMarker
                    ? customMarker
                    : <MarkerFormatted center={center} >
                      {pinLabel
                        && <Tooltip
                          direction='right'
                          offset={[15, -10]}
                          opacity={1}
                          permanent
                        >
                          {pinLabel}
                        </Tooltip>
                      }
                    </MarkerFormatted>
                  }
                </>
              }
              {center && !blurred && <ZoomControls bottom={3} right={2} />}
              {allowSearch && <InlayMapSearch disableMapAnimation={disableSearchMapAnimation} onAddressClick={onAddressClick} />}
              {entityId && center && <ViewMapButton entityId={entityId} />}
              {allowFullScreen && !smallScreen &&
                <Box top={theme.spacing(2)} right={theme.spacing(2)} position='absolute' zIndex={800}>
                  <MaximizeButton color='primary' data-testid='map-open-full-screen-button' onClick={onOpenInFullClick}>
                    <Icon icon="Maximize" style={{ fontSize: '1.5rem' }} />
                  </MaximizeButton>
                </Box>
              }
              {center && children}
            </SearchProvider>
          </MapContainer>
        </FlexBox>
        <FlexBox
          sx={{
            alignItems: 'center',
            bottom: 0,
            justifyContent: 'center',
            left: 0,
            position: 'absolute',
            right: 0,
            top: 0,
          }}
        >
          <Typography
            data-testid='inlay-map-no-location-label'
            color="text.secondary"
            fontStyle="italic"
            style={{
              textShadow: `0 0 ${theme.spacing(1)} ${theme.palette.background.paper}, 0 0 ${theme.spacing(2)} ${theme.palette.background.paper}, 0 0 ${theme.spacing(3)} ${theme.palette.background.paper}`,
            }}
            variant="body2"
          >
            {overlayLabel}
            {(center && !blurred && !overlayLabel) && t('common:component.map.hint.no-location')}
          </Typography>
        </FlexBox>
      </InlayMapRoot>
    </MapProvider>
  );

  return renderMapLayersProvider ? (
    <MapLayersProvider shouldUseLocalStorage={shouldUseLocalStorage}>{body}</MapLayersProvider>
  ) : body;
};
